import React, { useEffect, useState } from "react";

import { useAutoAnimate } from "@formkit/auto-animate/react";
import * as Sentry from "@sentry/react";
import { every, find, isEmpty, values } from "lodash/fp";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Typography from "@mui/material/Typography";

import ActionBar from "@app/analytics/components/ActionBar";
import DateSelector from "@app/analytics/components/DateSelector";
import FilterChip from "@app/analytics/components/FilterChip";
import { getCalendarModifyAuthUrl } from "@app/analytics/hooks/calendar/useConnectCalendar";
import useRecommendations from "@app/analytics/hooks/recommendations/useRecommendations";
import * as Events from "@app/analytics/tracking/events";
import type {
  GroupedRecommendation,
  Recommendation,
  RecommendationAction,
} from "@app/analytics/types";
import * as RecommendationUtils from "@app/analytics/utils/recommendationUtils";
import useApps from "@app/common/hooks/useApps";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import { showToast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import { isResponseErrorWithStatus } from "@app/common/utils";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import AutoAnimateContainer from "@rui/AutoAnimateContainer";
import Button from "@rui/Button";
import ConfirmationModal from "@rui/ConfirmationModal";
import LoadSpinner from "@rui/LoadSpinner";
import Stack, { Row } from "@rui/Stack";
import { StickyContainer } from "@rui/styles";
import Tabs from "@rui/Tabs";

import RecommendationCategory from "./components/RecommendationCategory";
import RecommendationSummary from "./components/RecommendationSummary";
import {
  ALL_RESULTS_FILTER,
  RECOMMENDATION_ACTION_FILTERS,
  TABS,
} from "./constants";
import useFilteredRecommendations from "./hooks/useFilteredRecommendations";
import NoRecommendations from "./NoRecommendations";
import RecommendationCategoryDetail from "./RecommendationCategoryDetail";
import RecommendationDetail from "./RecommendationDetail";
import {
  Container,
  ContentContainer,
  FilterContainer,
  LeftPanel,
  LeftPanelContent,
  LeftPanelContentContainer,
  RightPanel,
  RightPanelContent,
  TabsContainer,
  Tab,
} from "./styles";
import type { RecommendationTab, RecommendationTabId } from "./types";
import { getGroupedRecommendationIndexByRecommendationId } from "./utils";

const Recommendations = () => {
  usePageTitle("Recommendations");

  const navigate = useNavigate();
  const { client } = useApps();
  const [queryParams] = useSearchParams();
  const { tabId } = useParams() as {
    tabId?: RecommendationTabId;
  };
  const [useSampleData, setUseSampleData] = useLocalStorage(
    "showSampleRecommendations",
    true
  );

  const recommendations = useRecommendations({
    enabled: true,
    useSample: useSampleData,
  });

  const [selectedTab, setSelectedTab] = useState<RecommendationTabId>(
    tabId ?? "new"
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [calendarProvider, setCalendarProvider] =
    useState<CalendarProviderId>("google_calendar");

  const {
    groupedPendingRecommendations: allGroupedPendingRecommendations,
    reviewedRecommendationsByCategory: allReviewedRecommendationsByCategory,
  } = recommendations;

  const recommendationIdQueryParam = queryParams.get("id");
  const isPostCalendarAuth = queryParams.get("calendarAuth") === "true";
  const isCalendarAuthSuccess = queryParams.get("success") === "true";

  const viewingPendingRecommendations = selectedTab === "new";

  const {
    appliedFilters,
    filterCounts,
    onClickFilter,
    filteredGroupedPendingRecommendations,
    filteredReviewedRecommendationsByCategory,
    pendingPagination,
    reviewedPagination,
    reviewedRecommendationCategories,
  } = useFilteredRecommendations(recommendations, selectedTab);

  // the filtered GroupedRecommendation may have some recommendations filtered out based on the applied
  // filters. we pass both to the right panel so that the user can "see all" even from a filtered state
  const filteredActivePendingRecommendation =
    filteredGroupedPendingRecommendations[pendingPagination.currentPage];
  const unfilteredActivePendingRecommendation = find(
    { id: filteredActivePendingRecommendation?.id },
    allGroupedPendingRecommendations
  );

  const activeReviewedRecommendationCategory =
    reviewedRecommendationCategories[reviewedPagination.currentPage];

  const [leftContainerRef] = useAutoAnimate();
  const [leftContainerCategoryRef] = useAutoAnimate();

  useEffect(() => {
    if (!isPostCalendarAuth) return;

    if (isCalendarAuthSuccess) {
      showToast({
        message: "Success, you may now accept recommendations!",
        severity: "success",
      });
    } else {
      showToast({
        message: "There was a problem obtaining edit access to your calendar.",
        severity: "error",
      });
    }
  }, [isPostCalendarAuth, isCalendarAuthSuccess]);

  useEffect(() => {
    if (!recommendationIdQueryParam) return;
    const page = getGroupedRecommendationIndexByRecommendationId(
      filteredGroupedPendingRecommendations,
      recommendationIdQueryParam
    );
    if (page > 0) {
      pendingPagination.goToPage(page);
    }
  }, [recommendations.loading]);

  const handleDisableSampleData = () => setUseSampleData(false);

  const handleModalConfirm = () => {
    window.location.href = getCalendarModifyAuthUrl({
      client,
      calendarProvider,
      opts: {
        returnTo: window.location.pathname,
        queryParams: {
          calendarAuth: true,
          ...(unfilteredActivePendingRecommendation
            ? { id: unfilteredActivePendingRecommendation.id }
            : {}),
        },
      },
    });
  };

  const handleSelectTab = (tab: RecommendationTab) => () => {
    setSelectedTab(tab.id);
    navigate(tab.route);
  };

  const handleSelectGroupedPendingRecommendation = (page: number) => () => {
    if (recommendationIdQueryParam) {
      navigate(ROUTES.RECOMMENDATIONS, { replace: true });
    }
    pendingPagination.goToPage(page);
  };

  const onAcceptError = (error: any) => {
    // User needs to grant edit permission to their calendar
    if (isResponseErrorWithStatus(error, 403)) {
      error.response.json().then((data: any) => {
        setCalendarProvider(data.calendarProvider ?? "google_calendar");
        setIsConfirmOpen(true);
      });
    } else {
      Sentry.captureException(error);
      const isStaleRecommendation =
        isResponseErrorWithStatus(error, 400) ||
        isResponseErrorWithStatus(error, 409);

      const errorMessage = isStaleRecommendation
        ? "Sorry, this recommendation is no longer applicable. Please ignore the recommendation to continue."
        : "Sorry, there was a problem accepting the recommendation.";

      showToast({
        message: errorMessage,
        severity: "error",
      });
    }
  };

  const handleAcceptRecommendation = (action: RecommendationAction) => {
    Events.acceptRecommendation();
    recommendations.acceptRecommendation(
      { action },
      {
        onError: onAcceptError,
      }
    );
    showToast({
      message:
        action.kind === "acknowledge"
          ? "Recommendation acknowledged"
          : "Recommendation applied",
      severity: "info",
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
      disableClickAway: true,
    });
  };

  const handleRejectRecommendation = (recommendation: Recommendation) => {
    Events.rejectRecommendation();
    recommendations.rejectRecommendation(
      {
        action: RecommendationUtils.getDefaultAction(recommendation),
      },
      {
        onError: (error) => {
          Sentry.captureException(error);
          showToast({
            message: "Sorry, there was a problem ignoring this recommendation.",
            severity: "error",
          });
        },
      }
    );
    showToast({
      message: "Recommendation ignored",
      severity: "info",
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
      disableClickAway: true,
    });
  };

  const handleAcceptAll = (recommendation: GroupedRecommendation) => {
    recommendations.acceptAllRecommendations(recommendation, {
      onError: onAcceptError,
    });
  };

  const handleRejectAll = (recommendation: GroupedRecommendation) => {
    recommendations.rejectAllRecommendations(recommendation, {
      onError: (error) => {
        Sentry.captureException(error);
        showToast({
          severity: "error",
          message: "Sorry, there was a problem ignoring all recommendations",
        });
      },
    });
  };

  const handleToggleShowFilters = () => {
    setShowFilters((show) => !show);
  };

  const showEmptyState = viewingPendingRecommendations
    ? isEmpty(allGroupedPendingRecommendations)
    : every(isEmpty, values(allReviewedRecommendationsByCategory));

  const expandLeftPanel = recommendations.loading || showEmptyState;

  return (
    <>
      <Container>
        <ActionBar>
          <Typography variant="h2">Recommendations</Typography>
          <DateSelector fixedRange="60" />
        </ActionBar>
        <ContentContainer>
          <LeftPanel expanded={expandLeftPanel}>
            <TabsContainer>
              <Row variant="apart">
                <Tabs value={selectedTab}>
                  {TABS.map((tab) => (
                    <Tab
                      key={tab.id}
                      label={tab.label}
                      value={tab.id}
                      onClick={handleSelectTab(tab)}
                      count={
                        tab.id === "new"
                          ? allGroupedPendingRecommendations.length
                          : 0
                      }
                    />
                  ))}
                </Tabs>
                {!expandLeftPanel && (
                  <Button
                    variant="text"
                    sx={{ height: "fit-content", fontWeight: 400 }}
                    onClick={handleToggleShowFilters}
                  >
                    {showFilters ? "Hide" : "Show"} filters
                  </Button>
                )}
              </Row>
            </TabsContainer>
            <LeftPanelContentContainer expanded={expandLeftPanel}>
              {recommendations.loading ? (
                <Stack variant="center" flex={1}>
                  <LoadSpinner />
                </Stack>
              ) : showEmptyState ? (
                <Stack flex={1} paddingTop={2}>
                  <NoRecommendations />
                </Stack>
              ) : (
                <>
                  <StickyContainer>
                    <AutoAnimateContainer
                      show={showFilters}
                      sx={{ paddingBottom: { md: showFilters ? 0 : 1 } }}
                    >
                      <FilterContainer>
                        <FilterChip
                          label={ALL_RESULTS_FILTER.label}
                          onClick={onClickFilter(ALL_RESULTS_FILTER.id)}
                          isSelected={isEmpty(appliedFilters)}
                        />
                        {RECOMMENDATION_ACTION_FILTERS.map((filter) => {
                          const count = filterCounts[filter.id];
                          if (!count) return null;
                          return (
                            <FilterChip
                              key={filter.label}
                              label={filter.label}
                              onClick={onClickFilter(filter.id)}
                              numResults={count}
                              isSelected={appliedFilters.includes(filter.id)}
                            />
                          );
                        })}
                      </FilterContainer>
                    </AutoAnimateContainer>
                  </StickyContainer>
                  <LeftPanelContent ref={leftContainerRef}>
                    {viewingPendingRecommendations ? (
                      filteredGroupedPendingRecommendations.map((rec, i) => (
                        <RecommendationSummary
                          key={rec.id}
                          onAcceptAll={handleAcceptAll}
                          onClick={handleSelectGroupedPendingRecommendation(i)}
                          onRejectAll={handleRejectAll}
                          recommendation={rec}
                          selected={i === pendingPagination.currentPage}
                        />
                      ))
                    ) : (
                      <Stack spacing={1} ref={leftContainerCategoryRef}>
                        {reviewedRecommendationCategories.map((category, i) => (
                          <RecommendationCategory
                            key={category}
                            category={category}
                            onClick={() => reviewedPagination.goToPage(i)}
                            recommendations={
                              filteredReviewedRecommendationsByCategory[
                                category
                              ]
                            }
                          />
                        ))}
                      </Stack>
                    )}
                  </LeftPanelContent>
                </>
              )}
            </LeftPanelContentContainer>
          </LeftPanel>
          <RightPanel expanded={!expandLeftPanel}>
            <RightPanelContent>
              {viewingPendingRecommendations ? (
                <RecommendationDetail
                  actionLoading={recommendations.actionIsLoading}
                  filteredRecommendation={filteredActivePendingRecommendation}
                  isSample={recommendations.isSampleData}
                  onAcceptAll={handleAcceptAll}
                  onAcceptRecommendation={handleAcceptRecommendation}
                  onRejectAll={handleRejectAll}
                  onRejectRecommendation={handleRejectRecommendation}
                  onDisableSampleData={handleDisableSampleData}
                  pagination={pendingPagination}
                  unfilteredRecommendation={
                    unfilteredActivePendingRecommendation
                  }
                />
              ) : (
                <RecommendationCategoryDetail
                  category={activeReviewedRecommendationCategory}
                  pagination={reviewedPagination}
                  filteredRecommendations={
                    filteredReviewedRecommendationsByCategory[
                      activeReviewedRecommendationCategory
                    ]
                  }
                  unfilteredRecommendations={
                    allReviewedRecommendationsByCategory[
                      activeReviewedRecommendationCategory
                    ]
                  }
                />
              )}
            </RightPanelContent>
          </RightPanel>
        </ContentContainer>
      </Container>

      {/* Dialogs */}
      <ConfirmationModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleModalConfirm}
        title="Heads up!"
        confirmProps={{ label: "Yes, continue" }}
        cancelProps={{ label: "Not now" }}
      >
        <Typography>
          To accept recommendations, Read needs to be able to edit your
          calendar. Do you want to grant edit permission?
        </Typography>
      </ConfirmationModal>
    </>
  );
};

export default Recommendations;
